import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import { cn } from '@/lib/utils'
import type { OrderStats } from '@/types/api'
import { useQuery } from '@tanstack/react-query'
import { PackageCheck } from 'lucide-react'
import React, { useEffect } from 'react'

interface OrderStatusWidgetProps {
  className?: string
}

const STATUS_ITEMS: Array<{
  key: keyof Pick<
    OrderStats,
    | 'total_pending'
    | 'total_review'
    | 'total_processing'
    | 'total_shipped'
    | 'total_delivered'
    | 'total_cancelled'
    | 'total_refunded'
  >
  label: string
  tone: string
}> = [
  { key: 'total_pending', label: 'Pending', tone: 'border-amber-200 bg-amber-50 text-amber-700' },
  { key: 'total_review', label: 'Review', tone: 'border-slate-200 bg-slate-50 text-slate-700' },
  {
    key: 'total_processing',
    label: 'Processing',
    tone: 'border-blue-200 bg-blue-50 text-blue-700',
  },
  {
    key: 'total_shipped',
    label: 'Shipped',
    tone: 'border-violet-200 bg-violet-50 text-violet-700',
  },
  {
    key: 'total_delivered',
    label: 'Delivered',
    tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  },
  { key: 'total_cancelled', label: 'Cancelled', tone: 'border-rose-200 bg-rose-50 text-rose-700' },
  {
    key: 'total_refunded',
    label: 'Refunded',
    tone: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700',
  },
]

export const OrderStatusWidget: React.FC<OrderStatusWidgetProps> = ({ className }) => {
  const { toast } = useToast()

  const { data, isLoading, error } = useQuery({
    queryKey: ['orderStats', 'all'],
    queryFn: () => apiClient.getOrderStats({ period: 'all' }),
  })

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to load order status overview',
        variant: 'destructive',
      })
    }
  }, [error, toast])

  const stats = data?.data
  const items = STATUS_ITEMS.filter(
    (item) => (stats?.[item.key] ?? 0) > 0 || item.key !== 'total_refunded'
  )
  const totalOrders = stats?.total_orders ?? 0
  const queueCount = (stats?.total_pending ?? 0) + (stats?.total_review ?? 0)
  const fulfillmentCount = (stats?.total_processing ?? 0) + (stats?.total_shipped ?? 0)
  const closedCount =
    (stats?.total_delivered ?? 0) + (stats?.total_cancelled ?? 0) + (stats?.total_refunded ?? 0)

  const summaryRows = [
    {
      label: 'In Queue',
      value: queueCount,
      tone: 'bg-amber-500',
    },
    {
      label: 'In Fulfillment',
      value: fulfillmentCount,
      tone: 'bg-blue-600',
    },
    {
      label: 'Closed Orders',
      value: closedCount,
      tone: 'bg-emerald-600',
    },
  ]

  return (
    <Card
      className={cn(
        'flex h-full flex-col rounded-3xl border-slate-200/80 bg-white shadow-sm',
        className
      )}
    >
      <CardHeader className="border-b border-slate-100 pb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-lg font-semibold text-slate-950">Order Status</CardTitle>
            <CardDescription className="mt-1 text-sm text-slate-500">
              All-time distribution of orders across the fulfillment pipeline.
            </CardDescription>
          </div>
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-2.5 text-blue-700">
            <PackageCheck className="h-5 w-5" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 p-4">
        {isLoading ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-24 rounded-2xl" />
              ))}
            </div>
            <Skeleton className="h-36 rounded-2xl" />
          </>
        ) : stats ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((item) => (
                <div key={item.key} className={cn('rounded-2xl border px-4 py-3', item.tone)}>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] opacity-80">
                    {item.label}
                  </div>
                  <div className="mt-3 text-3xl font-semibold tracking-tight">
                    {stats[item.key]}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
              <div className="text-sm font-semibold text-slate-900">Pipeline Summary</div>
              <div className="mt-4 space-y-3">
                {summaryRows.map((row) => {
                  const percentage = totalOrders > 0 ? (row.value / totalOrders) * 100 : 0
                  return (
                    <div key={row.label}>
                      <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
                        <span className="font-medium">{row.label}</span>
                        <span>{row.value}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                        <div
                          className={cn('h-full rounded-full', row.tone)}
                          style={{ width: `${Math.max(percentage, row.value > 0 ? 6 : 0)}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}
